import { DOCUMENT } from '@angular/common';
import { inject, RendererFactory2 } from '@angular/core';
import { RedirectCommand, type ResolveFn, Router } from '@angular/router';
import type { ISitemapNode } from '@informally/common/sitemaps';
import { ACTIVE_SHOP_DATA } from '@informally/frontend/active-shop';
import { TranslateService } from '@ngx-translate/core';
import { FiltersChannelsService } from '@suvo/frontend-framework/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/features/filters/services/filters-channels.service';
import type { FromAPI } from '@suvo/shared/interface-utils';
import { SEOService } from '../../features/services/seo.service';
import type { IDynamicRouteData } from '../interfaces/dynamic-route-data.interface';
import {
  DynamicRouteService,
  type SitemapNodeWithChildren,
} from '../services/dynamic-route.service';

/** Higher-order function that creates a dynamic route resolver for the given `treeType`. */
export const createDynamicRouteResolver =
  (treeType: 'private' | 'public'): ResolveFn<IDynamicRouteData> =>
  async (route) => {
    const logPrefix = '[dynamic-route-resolver]';
    const tracePrefix = '[dynamic-route-resolver:trace]';
    const document = inject(DOCUMENT);
    const filtersChannelService = inject(FiltersChannelsService);
    const renderer = inject(RendererFactory2).createRenderer(null, null);
    const router = inject(Router);
    const seoService = inject(SEOService);
    const shopData = inject(ACTIVE_SHOP_DATA);
    const sitemapNodeService = inject(DynamicRouteService);

    const lang = inject(TranslateService).currentLang;
    const defaultLang = inject(TranslateService).defaultLang;

    const pathSegments = route.url.map(({ path }) => path);

    const routePrefix = sitemapNodeService.getRoutePrefix(treeType);

    // Keep a global counter to verify resolver execution from DevTools.
    const globalScope = globalThis as typeof globalThis & {
      __dynamicRouteResolverHits?: number;
    };
    const hitCount = (globalScope.__dynamicRouteResolverHits ?? 0) + 1;
    globalScope.__dynamicRouteResolverHits = hitCount;

    console.info(tracePrefix, 'invoked', {
      hitCount,
      treeType,
      routeConfigPath: route.routeConfig?.path,
      routeParamMapKeys: route.paramMap.keys,
      routeQueryParamKeys: route.queryParamMap.keys,
      incomingPathSegments: [...pathSegments],
      routePrefix,
    });

    console.debug(logPrefix, 'start', {
      treeType,
      incomingPathSegments: [...pathSegments],
      routePrefix,
      lang,
      defaultLang,
    });

    let pathFull = '/';
    let routeData: IDynamicRouteData | undefined;
    const subtreeCache = new Map<string, SitemapNodeWithChildren[]>();

    async function getChildSubtree(parentId?: string): Promise<SitemapNodeWithChildren[]> {
      const key = parentId ?? '<root>';
      const cached = subtreeCache.get(key);

      if (cached) {
        return cached;
      }

      const nodes = await sitemapNodeService.getChildNodes(
        parentId ? { treeType, parentId, includeTree: true } : { treeType, includeTree: true },
      );

      subtreeCache.set(key, nodes);
      return nodes;
    }

    async function getUseableRoute(): Promise<IDynamicRouteData | undefined> {
      let langToUse = lang ? lang : defaultLang;

      const branch = await sitemapNodeService.getBranch({
        treeType,
        lang: langToUse,
        pathFull: (pathFull = `/${pathSegments.join('/')}`),
        includeTree: true,
      });

      console.debug(logPrefix, 'getUseableRoute.branch', {
        pathFull,
        branchLength: branch?.length ?? 0,
      });

      if (branch) {
        const current = branch.at(-1);

        if (!current) {
          console.debug(logPrefix, 'getUseableRoute.noCurrentNode', {
            pathFull,
            branchLength: branch.length,
          });
          return;
        }

        const variant = sitemapNodeService.resolveVariant(current);
        const useable = sitemapNodeService.isRouteUseable(current, variant);

        console.debug(logPrefix, 'getUseableRoute.current', {
          currentId: current?._id,
          routeType: current?.routeType,
          variantPathSegment: variant?.pathSegment,
          variantDashboardUuid: variant?.dashboardUuid,
          useable,
        });

        routeData = { branch, current, variant };

        if (Array.isArray(current.children)) {
          subtreeCache.set(current._id, current.children);
        }

        if (useable) {
          if (current.routeType === 'GROUP') {
            console.info(tracePrefix, 'group.routeIsUseable', {
              hitCount,
              treeType,
              currentId: current._id,
              currentRouteType: current.routeType,
              pathFull,
              willTryFirstChildRedirect: treeType === 'private',
            });
          }

          return routeData;
        }
      }
    }

    if ((await getUseableRoute()) && routeData) {
      if (treeType === 'private' && routeData.current.routeType === 'GROUP') {
        console.info(tracePrefix, 'group.useable.tryFirstChildRedirect', {
          hitCount,
          groupId: routeData.current._id,
          pathFull,
        });

        const firstChild = (await getChildSubtree(routeData.current._id)).at(0);

        console.info(tracePrefix, 'group.useable.firstChild.lookupResult', {
          hitCount,
          groupId: routeData.current._id,
          foundFirstChild: !!firstChild,
          firstChildId: firstChild?._id,
          firstChildRouteType: firstChild?.routeType,
        });

        if (firstChild) {
          const firstChildVariant = sitemapNodeService.resolveVariant(firstChild);

          if (firstChildVariant) {
            pathSegments.push(firstChildVariant.pathSegment);

            console.info(tracePrefix, 'redirect.groupFirstChild.useableGroup', {
              hitCount,
              groupId: routeData.current._id,
              firstChildId: firstChild._id,
              redirectPathSegments: [...pathSegments],
            });

            return createRedirect();
          }

          console.info(tracePrefix, 'group.useable.firstChild.noVariant', {
            hitCount,
            firstChildId: firstChild._id,
          });
        }

        console.info(tracePrefix, 'group.useable.firstChildRedirectSkipped', {
          hitCount,
          groupId: routeData.current._id,
          reason: firstChild ? 'first-child-has-no-variant' : 'no-first-child',
        });
      }

      for (let i = routeData.branch.length - 1; i >= 0; i--) {
        if ((routeData.filterGroup = routeData.branch[i].filterGroup)) {
          routeData.filtersChannel = filtersChannelService.getFiltersChannel(
            routeData.filterGroup,
            '',
            '',
            'GENERAL',
          );
          break;
        }
      }

      /////////
      // SEO //
      /////////

      if (routeData.variant?.description) {
        seoService.updateMetaTagKv('description', routeData.variant.description);
      }

      seoService.setTitle(routeData.variant?.title ?? routeData.variant?.name);

      seoService.clearHreflang();

      const primaryWebUrl = (await shopData()).tenant.primaryWebUrl;
      const hreflangPrefix = primaryWebUrl + routePrefix;
      const canonicalUrl = hreflangPrefix + (routeData.variant?.pathFull || '');

      seoService.setCanonical(canonicalUrl);
      seoService.setOpenGraphUrl(canonicalUrl);

      for (const variant of routeData.current.variants) {
        const link = renderer.createElement('link');
        renderer.setAttribute(link, 'rel', 'alternate');
        renderer.setAttribute(link, 'hreflang', variant.lang);
        renderer.setAttribute(link, 'href', hreflangPrefix + variant.pathFull);
        renderer.appendChild(document.head, link);
      }

      /////////////
      // End SEO //
      /////////////

      console.info(tracePrefix, 'resolved', {
        hitCount,
        currentId: routeData.current?._id,
        currentRouteType: routeData.current?.routeType,
        variantPathFull: routeData.variant?.pathFull,
      });

      return routeData;
    }

    function createRedirect() {
      console.debug(logPrefix, 'createRedirect.beforePrefix', {
        routePrefix,
        pathSegments: [...pathSegments],
      });

      if (routePrefix) {
        pathSegments.unshift(routePrefix);
      }

      console.debug(logPrefix, 'createRedirect.afterPrefix', {
        pathSegments: [...pathSegments],
      });

      return new RedirectCommand(router.createUrlTree(pathSegments));
    }

    // Pop path segments until a useable route is found
    while (!routeData && pathSegments.length) {
      console.debug(logPrefix, 'fallback.popSegment.before', {
        pathSegments: [...pathSegments],
      });

      pathSegments.pop();

      console.debug(logPrefix, 'fallback.popSegment.after', {
        pathSegments: [...pathSegments],
      });

      if (await getUseableRoute()) {
        console.debug(logPrefix, 'fallback.popSegment.redirectFound', {
          pathSegments: [...pathSegments],
        });
        console.info(tracePrefix, 'redirect.fallbackPop', {
          hitCount,
          redirectPathSegments: [...pathSegments],
        });
        return createRedirect();
      }
      // TODO: valid route but not useable - continue to below
    }

    // The route isn't useable. Recursively check children for a useable route.
    // TODO: Allow tenant to explicitly set the default child routes to redirect to in these cases

    const shouldRecursiveSearchForRedirect =
      pathFull === '/' || (routeData && !sitemapNodeService.isRouteUseable(routeData.current));

    console.info(tracePrefix, 'group.firstChildRedirectDecision', {
      hitCount,
      pathFull,
      shouldRecursiveSearchForRedirect,
      hasRouteData: !!routeData,
      currentId: routeData?.current?._id,
      currentRouteType: routeData?.current?.routeType,
      currentUseable: routeData ? sitemapNodeService.isRouteUseable(routeData.current) : undefined,
      reason:
        pathFull === '/'
          ? 'root-path'
          : routeData && !sitemapNodeService.isRouteUseable(routeData.current)
            ? 'route-not-useable'
            : 'route-useable-or-missing-data',
    });

    if (shouldRecursiveSearchForRedirect) {
      console.debug(logPrefix, 'recursiveSearch.enter', {
        pathFull,
        hasRouteData: !!routeData,
        currentId: routeData?.current?._id,
        currentRouteType: routeData?.current?.routeType,
      });

      if (treeType === 'public') {
        const langToUse = lang ?? defaultLang ?? 'en';
        console.debug(logPrefix, 'public.redirectHome', { langToUse });
        console.info(tracePrefix, 'redirect.publicHome', { hitCount, langToUse });
        return new RedirectCommand(router.createUrlTree([langToUse, 'home']));
      }

      // Group routes should always forward to their first child node.
      if (routeData?.current.routeType === 'GROUP') {
        console.debug(logPrefix, 'group.forwardToFirstChild.start', {
          groupId: routeData.current._id,
        });

        const firstChild = (await getChildSubtree(routeData.current._id)).at(0);

        console.info(tracePrefix, 'group.firstChild.lookupResult', {
          hitCount,
          groupId: routeData.current._id,
          foundFirstChild: !!firstChild,
          firstChildId: firstChild?._id,
          firstChildRouteType: firstChild?.routeType,
        });

        if (firstChild) {
          const firstChildVariant = sitemapNodeService.resolveVariant(firstChild);

          console.debug(logPrefix, 'group.forwardToFirstChild.found', {
            firstChildId: firstChild._id,
            firstChildRouteType: firstChild.routeType,
            firstChildVariantPathSegment: firstChildVariant?.pathSegment,
          });

          if (firstChildVariant) {
            pathSegments.push(firstChildVariant.pathSegment);
            console.debug(logPrefix, 'group.forwardToFirstChild.redirect', {
              pathSegments: [...pathSegments],
            });
            console.info(tracePrefix, 'redirect.groupFirstChild', {
              hitCount,
              firstChildId: firstChild._id,
              redirectPathSegments: [...pathSegments],
            });
            return createRedirect();
          } else {
            console.info(tracePrefix, 'group.firstChild.noVariant', {
              hitCount,
              firstChildId: firstChild._id,
            });
          }
        } else {
          console.debug(logPrefix, 'group.forwardToFirstChild.none');
        }
      }

      const rootNodes = await getChildSubtree(routeData ? routeData.current._id : undefined);

      const visitNodes = (nodes: SitemapNodeWithChildren[], depth: number): boolean => {
        for (const node of nodes) {
          const variant = sitemapNodeService.resolveVariant(node);

          if (!variant) {
            continue;
          }

          const useable = sitemapNodeService.isRouteUseable(node, variant);

          console.debug(logPrefix, 'recursiveSearch.visitNode', {
            nodeId: node._id,
            nodeRouteType: node.routeType,
            variantPathSegment: variant.pathSegment,
            variantDashboardUuid: variant.dashboardUuid,
            useable,
            depth,
          });

          pathSegments.push(variant.pathSegment);

          if (useable) {
            console.debug(logPrefix, 'recursiveSearch.redirect', {
              pathSegments: [...pathSegments],
            });
            console.info(tracePrefix, 'redirect.recursiveSearch', {
              hitCount,
              nodeId: node._id,
              redirectPathSegments: [...pathSegments],
            });
            return true;
          }

          if (visitNodes(node.children ?? [], depth + 1)) {
            return true;
          }

          pathSegments.pop();
        }

        return false;
      };

      if (visitNodes(rootNodes, 1)) {
        return createRedirect();
      }
    }

    console.error(tracePrefix, 'failed', {
      hitCount,
      treeType,
      pathFull,
    });

    throw Error(
      `Failed to resolve a route within the ${treeType} sitemap for full path '${pathFull}'`,
    );
  };
